import { useState, useEffect } from 'react';
import { markPaywallShown } from '@/utils/usageStorage';

type PaymentTab = 'wechat' | 'alipay';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PaywallModal({ isOpen, onClose }: PaywallModalProps) {
  const [activeTab, setActiveTab] = useState<PaymentTab>('wechat');

  // 弹窗打开时，标记支付弹窗已经被触发过
  useEffect(() => {
    if (isOpen) {
      markPaywallShown();
      setActiveTab('wechat');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 遮罩 */}
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 弹窗 */}
      <div className="relative w-full max-w-sm bg-background-50 rounded-2xl border border-background-200 overflow-hidden animate-slide-up">
        {/* 头部 */}
        <div className="bg-accent-500 px-5 pt-6 pb-5 text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-background-50/20 rounded-full">
            <i className="ri-coins-line text-2xl text-background-50"></i>
          </div>
          <h3 className="font-heading text-lg font-bold text-background-50 mb-1">免费次数已用完</h3>
          <p className="text-sm text-background-50/80">扫码付费解锁更多次数</p>
        </div>

        {/* 内容 */}
        <div className="px-5 pt-5 pb-4">
          <div className="bg-background-100 rounded-xl border border-background-200 p-4 mb-4 text-center">
            <p className="text-sm text-foreground-500 mb-1">当前剩余次数</p>
            <p className="font-heading text-3xl font-bold text-accent-600">0</p>
          </div>

          <div className="bg-primary-50 rounded-xl border border-primary-200 p-4 mb-5 text-center">
            <p className="text-sm text-primary-700 font-medium mb-1">扫码付费 3.90 元</p>
            <p className="text-xs text-primary-600">立即获得 30 次使用次数</p>
          </div>

          {/* 支付方式页签 */}
          <div className="mb-3">
            <div className="flex bg-background-100 rounded-full p-1 border border-background-200">
              <button
                onClick={() => setActiveTab('wechat')}
                className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
                  activeTab === 'wechat'
                    ? 'bg-secondary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-wechat-line text-base"></i>
                </div>
                <span>微信支付</span>
              </button>
              <button
                onClick={() => setActiveTab('alipay')}
                className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
                  activeTab === 'alipay'
                    ? 'bg-secondary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bank-card-line text-base"></i>
                </div>
                <span>支付宝</span>
              </button>
            </div>
          </div>

          {/* 二维码展示区 - 根据选中页签切换 */}
          <div className="mb-5">
            {activeTab === 'wechat' ? (
              <div className="bg-background-100 rounded-xl border border-background-200 overflow-hidden">
                <div className="aspect-square w-full max-w-[260px] mx-auto p-4">
                  <img
                    src="https://storage.helloreaddy.io/project_files/74e88f2c-3689-4ad7-91de-a011c3f37216/ca021b4f-7397-4aa8-8944-2384547cc6ef_compressed_unnamed.webp"
                    alt="微信收款码 - 长按识别二维码支付"
                    className="w-full h-full object-contain rounded-lg"
                  />
                </div>
                <div className="py-3 text-center bg-background-50 border-t border-background-200">
                  <span className="text-sm text-foreground-700 font-medium">
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-wechat-line text-secondary-500"></i>
                    </div>
                    微信扫码支付 3.90 元
                  </span>
                  <p className="text-xs text-foreground-400 mt-0.5">长按二维码识别，或使用另一台手机扫码</p>
                </div>
              </div>
            ) : (
              <div className="bg-background-100 rounded-xl border border-background-200 overflow-hidden">
                <div className="aspect-square w-full max-w-[260px] mx-auto p-4">
                  <img
                    src="https://storage.helloreaddy.io/project_files/74e88f2c-3689-4ad7-91de-a011c3f37216/33812e37-a241-4fb4-8d5d-c9f9e874e3fa_compressed_unnamed.webp"
                    alt="支付宝收款码 - 长按识别二维码支付"
                    className="w-full h-full object-contain rounded-lg"
                  />
                </div>
                <div className="py-3 text-center bg-background-50 border-t border-background-200">
                  <span className="text-sm text-foreground-700 font-medium">
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-bank-card-line text-secondary-500"></i>
                    </div>
                    支付宝扫码支付 3.90 元
                  </span>
                  <p className="text-xs text-foreground-400 mt-0.5">长按二维码识别，或使用另一台手机扫码</p>
                </div>
              </div>
            )}
          </div>

          {/* 支付确认按钮 */}
          <button
            onClick={onClose}
            className="w-full py-3.5 rounded-xl bg-primary-500 text-background-50 font-heading text-base font-bold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-[0.98] mb-3"
          >
            我已完成支付
          </button>

          <p className="text-xs text-foreground-400 text-center">
            支付后请前往「我的」页面解锁次数
          </p>
        </div>

        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/30 hover:bg-background-50/50 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg text-background-50"></i>
        </button>
      </div>
    </div>
  );
}